import { Component, Input, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { RoleNames, RoutePath } from '../../routing/routes';
import { animateText, onSideNavChange } from '../../animations/animations';
import { SidenavService } from '../../services/sidenav/sidenav.service';
import { AuthService, UserService } from 'app/core/services';
import { Subscription, TeardownLogic } from 'rxjs';
import { UserInfo } from 'app/core/services/user/user';
import { ConfirmationDialogOptions, ConfirmationDialogService } from 'app/shared/dialogs/confirmation';

export interface MenuLink {
    link?: string;
    icon?: string;
    text?: string;
    allowedRoles?: string[];
    params?: any;
}

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    animations: [onSideNavChange, animateText]
})
export class NavbarComponent implements OnDestroy {
    private _subrelease = new Subscription();
    get subrelease(): TeardownLogic {
        return this._subrelease;
    }
    set subrelease(sub: TeardownLogic) {
        this._subrelease.add(sub);
    }
    
    RoutePath = RoutePath;
    user: UserInfo | null = null;
    currentRole?: string | null = null;

    menuLinks: MenuLink[] = [];

    public sideNavState: boolean = true;
    public linkText: boolean = true;

    SchoolApplicationsRoles: string[] = [
        RoleNames.ProgramContact, 
        RoleNames.Principal, 
        RoleNames.AssistantPrin, 
        RoleNames.Counselor, 
        RoleNames.SuccessCoach, 
        RoleNames.StudentAdvisor, 
        RoleNames.Designee, 
    ];

    @Input('hideMiniButton') hideMiniButton: boolean = false;
    @Input('sidenav') sidenav!: MatSidenav;

    constructor(
        public dialog: MatDialog, 
        private authService: AuthService,
        private _sidenavService: SidenavService, 
        private confirmationDialogService: ConfirmationDialogService,
        private userService: UserService,
    ) {
        const self = this;
        self.user = self.userService.User;
        self.currentRole = self.user?.CurrentRole;

        self.subrelease = self.userService.UserSubject.subscribe((_user) => {
            self.user = _user;
            
            self.menuLinks = [
                { 
                    link: RoutePath.Home,
                    icon: 'home',
                    text: 'HOME PAGE',
                },
                { 
                    link: RoutePath.StudentSearch,
                    icon: 'search',
                    text: 'STUDENT SEARCH',
                    allowedRoles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin, 
                        RoleNames.Clearinghouse, 
                        RoleNames.ProgramContact, 
                        RoleNames.DistrictStaff, 
                        RoleNames.Principal, 
                        RoleNames.AssistantPrin, 
                        RoleNames.Counselor, 
                        RoleNames.SuccessCoach, 
                        RoleNames.StudentAdvisor, 
                        RoleNames.Designee, 
                        RoleNames.Reader 
                    ], 
                    params: { Source: this.user?.CurrentRole || '' }
                },
                { 
                    link: RoutePath.ViewStudent,
                    icon: 'person',
                    text: 'VIEW STUDENT',
                    allowedRoles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin, 
                        RoleNames.Clearinghouse, 
                        RoleNames.ProgramContact, 
                        RoleNames.DistrictStaff, 
                        RoleNames.Principal, 
                        RoleNames.AssistantPrin, 
                        RoleNames.Counselor, 
                        RoleNames.SuccessCoach, 
                        RoleNames.StudentAdvisor, 
                        RoleNames.Designee, 
                        RoleNames.Reader 
                    ],
                },
                { 
                    link: RoutePath.PendingApplications,
                    icon: 'assignment_late',
                    text: 'PENDING APPLICATIONS',
                    allowedRoles: [
                        RoleNames.Developer,
                        RoleNames.SystemAdmin,
                        RoleNames.Clearinghouse,
                        RoleNames.Reader 
                    ],
                },
                { 
                    link: RoutePath.MyApplications,
                    icon: 'assignment',
                    text: 'MY APPLICATIONS',
                    allowedRoles: [
                        RoleNames.DistrictStaff, 
                        RoleNames.Principal, 
                        RoleNames.AssistantPrin, 
                        RoleNames.Counselor, 
                        RoleNames.SuccessCoach, 
                        RoleNames.StudentAdvisor, 
                        RoleNames.Designee,
                    ],
                },
                { 
                    link: RoutePath.SchoolApplications,
                    icon: 'domain',
                    text: 'SCHOOL APPLICATIONS',
                    allowedRoles: [
                        RoleNames.ProgramContact, 
                        RoleNames.Principal, 
                        RoleNames.AssistantPrin, 
                        RoleNames.Counselor, 
                        RoleNames.SuccessCoach, 
                        RoleNames.StudentAdvisor, 
                        RoleNames.Designee, 
                    ],
                },
                { 
                    link: RoutePath.ProgramApplications,
                    icon: 'school',
                    text: 'PROGRAM APPLICATIONS',
                    allowedRoles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin, 
                        RoleNames.Clearinghouse, 
                        RoleNames.ProgramContact,
                        RoleNames.Reader  
                    ],
                },
                { 
                    link: RoutePath.WaitListApplications,
                    icon: 'schedule',
                    text: 'WAIT LIST APPLICATIONS',
                    allowedRoles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin, 
                        RoleNames.Clearinghouse,
                        RoleNames.Reader 
                    ],
                },
                { 
                    link: RoutePath.ManageDesignees,
                    icon: 'account_box',
                    text: 'MANAGE DESIGNEES',
                    allowedRoles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin 
                    ],
                },
                { 
                    link: RoutePath.ManagePrograms,
                    icon: 'school',
                    text: 'MANAGE PROGRAMS',
                    allowedRoles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin 
                    ],
                },
                // { 
                //     link: RoutePath.RolesPermissions,
                //     icon: 'person',
                //     text: 'ROLES & PERMISSIONS',
                //     allowedRoles: [
                //         RoleNames.Developer, 
                //         RoleNames.SystemAdmin 
                //     ],
                // },
                { 
                    link: RoutePath.ManageClearinghouse,
                    icon: 'people',
                    text: 'MANAGE CLEARINGHOUSE',
                    allowedRoles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin
                    ],
                },
                { 
                    link: RoutePath.ManageDistrictStaff,
                    icon: 'people',
                    text: 'MANAGE DISTRICT STAFF',
                    allowedRoles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin 
                    ],
                },
                { 
                    link: RoutePath.ManageReaders,
                    icon: 'people',
                    text: 'MANAGE READERS',
                    allowedRoles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin
                    ],
                },
                { 
                    link: RoutePath.Downloads,
                    icon: 'download',
                    text: 'DOWNLOAD PDFS',
                    allowedRoles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin, 
                        RoleNames.Clearinghouse, 
                        RoleNames.ProgramContact, 
                        RoleNames.Reader  
                    ],
                },
            ];
        });

        self._sidenavService.animateText$.subscribe( res => {
            self.linkText = res;
        });

        self._sidenavService.sideNavState$.subscribe( res => {
            self.sideNavState = res;
        });
    }

    logout(): void {
        const dialogRef = this.confirmationDialogService.Open({
            TitleText: 'Confirmation Requested...',
            MessageText: 'You are about to log out. Are you sure?',
            OKText: 'LOGOUT',
            CancelText: 'CANCEL',
        } as ConfirmationDialogOptions)

        dialogRef.subscribe(result => {
            if (result) {
                this.authService.Logout();
            }
        });
    }

    onSidenavToggle() {
        this.sideNavState = !this.sideNavState;
        
        setTimeout(() => {
          this._sidenavService.animateText$.next(this.sideNavState)
        }, 200)
        this._sidenavService.sideNavState$.next(this.sideNavState)
        this._sidenavService.mainContentChange$.next(this.sideNavState)
    }

    ngOnDestroy(): void {
        this._subrelease.unsubscribe();
    }
}